import axios from 'axios';

// Get API URL from environment variable or fallback to relative "/api" (dev proxy)
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const API_TIMEOUT = process.env.REACT_APP_API_TIMEOUT || 1800000;

// Debug: Log the API base URL to help diagnose issues
console.log('API Base URL configured:', API_BASE_URL);
console.log('REACT_APP_API_URL env var:', process.env.REACT_APP_API_URL || 'NOT SET');

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: parseInt(API_TIMEOUT), // 30 minutes for AI operations
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: false,
});

// Token migration: Migrate old token keys to new ones
const migrateTokensIfNeeded = () => {
  const newAccessKey = process.env.REACT_APP_ACCESS_TOKEN_KEY || 'draftjam_access_token';
  const newRefreshKey = process.env.REACT_APP_REFRESH_TOKEN_KEY || 'draftjam_refresh_token';
  const newUserDataKey = process.env.REACT_APP_USER_DATA_KEY || 'draftjam_user_data';
  
  const oldAccessKey = 'draftjam_access_token';
  const oldRefreshKey = 'draftjam_refresh_token';
  const oldUserDataKey = 'draftjam_user_data';
  
  // Only migrate if new keys are different from old keys
  if (newAccessKey !== oldAccessKey) {
    const oldAccessToken = localStorage.getItem(oldAccessKey);
    const oldRefreshToken = localStorage.getItem(oldRefreshKey);
    const oldUserData = localStorage.getItem(oldUserDataKey);
    
    if (oldAccessToken && !localStorage.getItem(newAccessKey)) {
      console.log('[Token Migration] Migrating tokens from old keys to new keys');
      localStorage.setItem(newAccessKey, oldAccessToken);
      if (oldRefreshToken) localStorage.setItem(newRefreshKey, oldRefreshToken);
      if (oldUserData) localStorage.setItem(newUserDataKey, oldUserData);
      
      // Clean up old keys
      localStorage.removeItem(oldAccessKey);
      localStorage.removeItem(oldRefreshKey);
      localStorage.removeItem(oldUserDataKey);
      console.log('[Token Migration] Migration complete');
    }
  }
};

// Run migration on module load
migrateTokensIfNeeded();

// Debug: Log environment variable values (these are baked in at build time)
// Use alert for maximum visibility - remove after debugging
if (typeof window !== 'undefined') {
  const envCheck = {
    REACT_APP_ACCESS_TOKEN_KEY: process.env.REACT_APP_ACCESS_TOKEN_KEY,
    REACT_APP_REFRESH_TOKEN_KEY: process.env.REACT_APP_REFRESH_TOKEN_KEY,
    REACT_APP_USER_DATA_KEY: process.env.REACT_APP_USER_DATA_KEY,
    NODE_ENV: process.env.NODE_ENV,
    computedAccessTokenKey: process.env.REACT_APP_ACCESS_TOKEN_KEY || 'draftjam_access_token',
    buildTime: new Date().toISOString()
  };
  console.error('🔴 [API INIT] Environment check:', envCheck);
  // Also log to window for easy access
  window.__API_ENV_CHECK = envCheck;
}

// Request interceptor to add auth headers
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const tokenKey = process.env.REACT_APP_ACCESS_TOKEN_KEY || 'draftjam_access_token';
    let token = localStorage.getItem(tokenKey);
    
    // Fallback: Check old key if new key doesn't have token
    if (!token && tokenKey !== 'draftjam_access_token') {
      token = localStorage.getItem('draftjam_access_token');
      if (token) {
        console.error('🟡 [API] Found token in old key, migrating...');
        localStorage.setItem(tokenKey, token);
        localStorage.removeItem('draftjam_access_token');
      }
    }
    
    // Debug: Always log for non-auth requests to diagnose issue
    if (config.url && !config.url.includes('/auth/login') && !config.url.includes('/auth/register') && !config.url.includes('/auth/refresh')) {
      const allLocalStorageKeys = Object.keys(localStorage).filter(k => k.includes('token') || k.includes('access') || k.includes('propulse') || k.includes('draftjam'));
      const debugInfo = {
        tokenKey,
        tokenFound: !!token,
        tokenLength: token?.length || 0,
        allTokenKeys: allLocalStorageKeys,
        localStorageSnapshot: allLocalStorageKeys.reduce((acc, k) => {
          const val = localStorage.getItem(k);
          acc[k] = val ? `${val.substring(0, 30)}...` : 'null';
          return acc;
        }, {})
      };
      console.error(`🔴 [API INTERCEPTOR] ${config.method} ${config.url}`, debugInfo);
      // Store on window for easy debugging
      window.__LAST_API_REQUEST = { url: config.url, ...debugInfo };
    }
    
    if (token) {
      // Ensure headers object exists
      if (!config.headers) {
        config.headers = {};
      }
      // Set Authorization header
      config.headers.Authorization = `Bearer ${token}`;
      console.error(`🟢 [API INTERCEPTOR] Authorization header SET for ${config.url}`);
    } else {
      // Log error if token is missing but request is being made (except for auth endpoints)
      if (config.url && !config.url.includes('/auth/login') && !config.url.includes('/auth/register') && !config.url.includes('/auth/refresh')) {
        console.error(`🔴 [API INTERCEPTOR] NO TOKEN FOUND for ${config.url}. Token key used: "${tokenKey}"`);
        console.error('🔴 [API INTERCEPTOR] All localStorage keys:', Object.keys(localStorage));
      }
    }
    
    // If sending FormData, delete Content-Type to let axios set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If token is expired and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem(process.env.REACT_APP_REFRESH_TOKEN_KEY || 'draftjam_refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken
          });
          
          const newAccessToken = response.data.access_token;
          localStorage.setItem(process.env.REACT_APP_ACCESS_TOKEN_KEY || 'draftjam_access_token', newAccessToken);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
          
        } catch (refreshError) {
          // Refresh failed, redirect to login
          localStorage.removeItem(process.env.REACT_APP_ACCESS_TOKEN_KEY || 'draftjam_access_token');
          localStorage.removeItem(process.env.REACT_APP_REFRESH_TOKEN_KEY || 'draftjam_refresh_token');
          localStorage.removeItem(process.env.REACT_APP_USER_DATA_KEY || 'draftjam_user_data');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }
    
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// API endpoints
export const endpoints = {
  auth: {
    login: '/auth/login',
    register: '/auth/register',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
    profile: '/auth/me',
  },
  dashboard: {
    stats: '/dashboard/stats',
  },
  prompt: {
    master: '/prompt/master',
    save: '/prompt/master',
  },
  proposals: {
    generate: '/proposals/generate',
    list: '/proposals/list',
    download: (filename) => `/proposals/download/${filename}`,
  },
  analysis: {
    data: '/analysis/data',
    run: '/analysis/run',
  },
  variables: {
    latest: '/variables/latest',
  },
  pipelines: {
    available: '/pipelines/available',
    status: '/pipelines/status',
  },
  context: {
    estimate: '/context/estimate',
  },
  lp: {
    chat: '/lp/chat',
    plan: (planId) => `/lp/plan/${planId}`,
    outline: (planId) => `/lp/plan/${planId}/outline`,
    node: (planId, nodeId) => `/lp/plan/${planId}/node/${nodeId}`,
    editNode: (planId, nodeId) => `/lp/plan/${planId}/node/${nodeId}/edit`,
    fromProposalAll: (planId) => `/lp/plan/${planId}/from-proposal/generate/`,
    fromProposalNode: (planId, nodeId) => `/lp/plan/${planId}/from-proposal/node/${nodeId}/generate/`,
    outlineParseDocument: '/lp/outline/parse-document/',
    outlineGenerateUnified: '/lp/outline/generate/',
    excelSlides: (planId) => `/lp/plan/${planId}/excel-slides/`,
    generateExcel: (planId) => `/lp/plan/${planId}/generate-excel/`,
    downloadExcel: (planId) => `/lp/plan/${planId}/excel-slides/download/`,
    excelEdit: (planId) => `/lp/plan/${planId}/excel/edit`,
    excelApply: (planId) => `/lp/plan/${planId}/excel/apply`,
    excelDiff: (planId) => `/lp/plan/${planId}/excel/diff`,
  },
  proposalOutline: {
    outline: (planId) => `/proposal/plan/${planId}/outline`,
    node: (planId, nodeId) => `/proposal/plan/${planId}/node/${nodeId}`,
    preliminaryProposal: (planId) => `/proposal/plan/${planId}/preliminary-proposal`,
  },
};

export const apiService = {
  // Dashboard
  getDashboardStats: () => api.get(endpoints.dashboard.stats),

  // Context estimation
  estimateContext: async (data) => {
    try {
      const response = await api.post(endpoints.context.estimate, data);
      return response.data;
    } catch (error) {
      console.error('Context estimation error:', error);
      throw error;
    }
  },

  // Prompt management
  getMasterPrompt: () => api.get(endpoints.prompt.master),
  saveMasterPrompt: (prompt) => {
    const formData = new FormData();
    formData.append('prompt', prompt);
    return api.post(endpoints.prompt.save, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Proposal generation
  generateProposal: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      const value = data[key];
      if (value === null || value === undefined) return;

      // JSON-encode selected resource arrays to avoid "[object Object]" in multipart
      if (
        key === 'selected_psychometrics' ||
        key === 'selected_frameworks' ||
        key === 'selected_tools' ||
        key === 'selected_games'
      ) {
        formData.append(key, JSON.stringify(value || []));
        return;
      }

      formData.append(key, value);
    });
    return api.post(endpoints.proposals.generate, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getGenerationProgress: (sessionId) => api.get(`/proposals/generate/progress/${sessionId}`),
  getGenerationResult: (sessionId) => api.get(`/proposals/generate/result/${sessionId}`),
  editProposalContent: (content, instruction, model = 'groq-compound') => 
    api.post('/proposals/edit', { content, instruction, model }),
  getProposals: () => api.get('/proposals/db/list'), // Fetch from MongoDB
  getProposalsFromMemory: () => api.get(endpoints.proposals.list), // Backward compatibility
  getProposalContent: (proposalId) => api.get(`/proposals/${proposalId}/content`),
  downloadProposal: (filename, format = 'pdf', onProgress) => api.get(
    `${endpoints.proposals.download(filename)}?format=${format}`,
    { 
      responseType: 'blob',
      onDownloadProgress: (evt) => {
        if (onProgress && evt.total) {
          const percent = Math.floor((evt.loaded / evt.total) * 100);
          try { onProgress(percent); } catch (_) {}
        }
      }
    }
  ),

  // AI content editing brain endpoint (legacy - immediate apply)
  brainEditContent: async (content, instruction) => {
    const response = await api.post('/proposals/edit/brain', { content, instruction });
    return response.data;
  },

  // New content-based discovery endpoint
  brainDiscoverDiffs: async (content, instruction, options = {}) => {
    const response = await api.post('/proposals/edit/discover', {
      content,
      instruction,
      ...options,
    });
    return response.data;
  },

  // Apply accepted diffs endpoint
  brainApplyDiffs: async (originalContent, memoryMap, acceptedDiffs) => {
    const response = await api.post('/proposals/edit/apply', {
      original_content: originalContent,
      memory_map: memoryMap,
      accepted_diffs: acceptedDiffs,
    });
    return response.data;
  },

  // Proposal outline management
  getProposalOutline: async (planId) => {
    const response = await api.get(endpoints.proposalOutline.outline(planId));
    return response.data;
  },
  getPreliminaryProposal: async (planId) => {
    const response = await api.get(endpoints.proposalOutline.preliminaryProposal(planId));
    return response.data;
  },
  saveProposalOutline: (planId, outline) =>
    api.post(endpoints.proposalOutline.outline(planId), { outline }),
  deleteProposalOutline: async (planId) => {
    const response = await api.delete(endpoints.proposalOutline.outline(planId));
    return response.data;
  },
  getProposalNode: async (planId, nodeId) => {
    const response = await api.get(endpoints.proposalOutline.node(planId, nodeId));
    return response.data;
  },
  saveProposalNode: (planId, nodeId, payload) =>
    api.post(endpoints.proposalOutline.node(planId, nodeId), payload),

  // Catalog helpers
  getCatalogItem: async (category, itemId) => {
    const response = await api.get(`/catalogs/${category}/${itemId}`);
    return response.data;
  },

  // Analysis
  getAnalysisData: () => api.get(endpoints.analysis.data),
  runAnalysis: (pipelineType, proposalsDir) => {
    const formData = new FormData();
    formData.append('pipeline_type', pipelineType);
    if (proposalsDir) {
      formData.append('proposals_dir', proposalsDir);
    }
    return api.post(endpoints.analysis.run, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Variables
  getLatestVariables: () => api.get(endpoints.variables.latest),

  // Pipelines
  getAvailablePipelines: () => api.get(endpoints.pipelines.available),
  getPipelineStatus: () => api.get(endpoints.pipelines.status),
  
  // Proposal Editing
  addProposalEdit: (proposalId, editData) => api.post(`/proposals/${proposalId}/add-edit`, editData),
  getProposalEdits: (proposalId) => api.get(`/proposals/${proposalId}/edits`),
  previewEdits: (proposalId) => api.post(`/proposals/${proposalId}/preview-edits`),
  applyAllEdits: (proposalId, data = {}) => api.post(`/proposals/${proposalId}/apply-all-edits`, data),
  deleteProposal: (proposalId) => api.delete(`/proposals/${proposalId}`),
  
  // Categories
  getCategories: () => api.get('/categories'),
  getCategoryInfo: (categoryName) => api.get(`/categories/${categoryName}`),
  analyzeCategory: (categoryName, pipelineType = 'single_prompt', analyzeNewOnly = false) => {
    const formData = new FormData();
    formData.append('pipeline_type', pipelineType);
    formData.append('analyze_new_only', analyzeNewOnly);
    return api.post(`/categories/${categoryName}/analyze`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getCategoryInsights: (categoryName) => api.get(`/categories/${categoryName}/insights`),
  getAllCategoriesSummary: () => api.get('/categories/summary/all'),
  createCategory: (categoryName, description = null) => {
    const formData = new FormData();
    formData.append('category_name', categoryName);
    if (description) {
      formData.append('description', description);
    }
    return api.post('/categories/create', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Excel Download Functions
  downloadSinglePromptExcel: async () => {
    try {
      const response = await api.get('/excel/download/single-prompt', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'single_prompt_analysis.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      throw new Error('Failed to download Excel file');
    }
  },

  downloadMultiStageExcel: async () => {
    try {
      const response = await api.get('/excel/download/multi-stage', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'multi_stage_analysis.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      throw new Error('Failed to download Excel file');
    }
  },

  downloadCategoryExcel: async (categoryName) => {
    try {
      const response = await api.get(`/excel/download/category/${categoryName}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${categoryName}_analysis.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      throw new Error('Failed to download Excel file');
    }
  },

  downloadLatestExcel: async () => {
    try {
      const response = await api.get('/excel/download/latest', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'latest_analysis.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      throw new Error('Failed to download Excel file');
    }
  },

  // Proposal Download Method (wrapped, returns response-like for existing callers)
  downloadProposalWrapped: async (filename, format, onProgress) => {
    try {
      const response = await api.get(`/proposals/download/${filename}?format=${format}`, { 
        responseType: 'blob',
        onDownloadProgress: (evt) => {
          if (onProgress && evt.total) {
            const percent = Math.floor((evt.loaded / evt.total) * 100);
            try { onProgress(percent); } catch (_) {}
          }
        }
      });
      // Return a response-like object that the frontend can handle
      return {
        status: response.status,
        ok: response.status >= 200 && response.status < 300,
        statusText: response.statusText || 'OK',
        blob: () => Promise.resolve(response.data)
      };
    } catch (error) {
      throw new Error(`Failed to download proposal (${format} format): ${error.message}`);
    }
  },

  // Get reference proposals for selection
  async getReferenceProposals() {
    try {
      const response = await api.get('/reference-proposals');
      // Debug logging
      if (response.data && response.data.proposals && response.data.proposals.length > 0) {
        const firstProposal = response.data.proposals[0];
        console.log('LP_DEBUG getReferenceProposals API response:', {
          proposalsCount: response.data.proposals.length,
          firstProposalId: firstProposal.id,
          firstProposalTitle: firstProposal.title,
          firstProposalHasContent: 'content' in firstProposal,
          firstProposalContentLength: firstProposal.content?.length || 0
        });
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching reference proposals:', error);
      throw error;
    }
  },

  // Upload proposal file for extraction
  async uploadProposalForExtraction(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/proposals/upload-for-extraction', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading proposal for extraction:', error);
      throw error;
    }
  },

  // Catalog Methods
  async getCatalogItems(category, page = 1, perPage = 20) {
    try {
      const response = await api.get(`/catalogs/${category}`, {
        params: { page, per_page: perPage }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching ${category} catalog:`, error);
      throw error;
    }
  },

  async searchCatalogs(query, category = '', page = 1, perPage = 20) {
    try {
      const response = await api.get('/catalogs/search', {
        params: { q: query, category, page, per_page: perPage }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching catalogs:', error);
      throw error;
    }
  },

  // Authentication Methods
  login: (credentials) => {
    console.log('API login called with:', credentials); // Debug log
    return api.post(endpoints.auth.login, credentials);
  },
  register: (userData) => {
    console.log('API register called with:', userData); // Debug log
    return api.post(endpoints.auth.register, userData);
  },
  logout: (refreshToken) => api.post(endpoints.auth.logout, { refresh_token: refreshToken }),
  refreshToken: (refreshToken) => api.post(endpoints.auth.refresh, { refresh_token: refreshToken }),
  getCurrentUser: (token) => {
    console.log('API getCurrentUser called with token:', !!token); // Debug log
    return api.get(endpoints.auth.profile, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },
  updateProfile: (profileData, token) => {
    return api.put(endpoints.auth.profile, profileData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },

  // Lesson Plan services
  lpChat: async ({ messages }) => {
    const payload = { messages };
    const res = await api.post(endpoints.lp.chat, payload);
    return res.data;
  },
  generateLessonPlanFromProposal: async (planId) => {
    const res = await api.post(endpoints.lp.fromProposalAll(planId));
    return res.data;
  },
  generateLessonPlanNodeFromProposal: async (planId, nodeId) => {
    const res = await api.post(endpoints.lp.fromProposalNode(planId, nodeId));
    return res.data;
  },
  getLessonPlanOutline: async (planId) => {
    const res = await api.get(endpoints.lp.outline(planId));
    return res.data;
  },
  saveLessonPlanOutline: async (planId, outline) => {
    const res = await api.post(endpoints.lp.outline(planId), { outline: outline });
    return res.data;
  },
  clearLpOutlineAndExcel: async (planId) => {
    const res = await api.delete(`/lp/plan/${planId}/outline/clear`);
    return res.data;
  },
  getLessonPlanNodeContent: async (planId, nodeId) => {
    const res = await api.get(endpoints.lp.node(planId, nodeId));
    return res.data;
  },
  saveLessonPlanNodeContent: async (planId, nodeId, content) => {
    const res = await api.post(endpoints.lp.node(planId, nodeId), { markdown_content: content });
    return res.data;
  },
  editLessonPlanNodeContent: async (planId, nodeId, editData) => {
    const res = await api.post(endpoints.lp.editNode(planId, nodeId), editData);
    return res.data;
  },
  parseLpDocument: async (formData) => {
    const res = await api.post(endpoints.lp.outlineParseDocument, formData);
    return res.data;
  },
  generateLpOutlineUnified: async (formData) => {
    // Content-Type will be set automatically by axios interceptor for FormData
    const res = await api.post(endpoints.lp.outlineGenerateUnified, formData);
    return res.data;
  },

  // Excel slide services
  getExcelSlides: async (planId) => {
    // Use shorter timeout for read operations (30 seconds instead of 30 minutes)
    const res = await api.get(endpoints.lp.excelSlides(planId), {
      timeout: 30000, // 30 seconds - should be fast for a simple read
    });
    return res.data;
  },
  generateExcelSlides: async (planId) => {
    const res = await api.post(endpoints.lp.generateExcel(planId));
    return res.data;
  },
  downloadExcelFile: async (planId) => {
    const res = await api.get(endpoints.lp.downloadExcel(planId), { responseType: 'blob' });
    return res.data;
  },
  
  // Excel editing services
  previewExcelEdits: async (planId, scope, instruction) => {
    const res = await api.post(endpoints.lp.excelEdit(planId), { scope, instruction });
    return res.data;
  },
  applyExcelPatches: async (planId, previewId, selectedPatches = null) => {
    const body = { preview_id: previewId };
    if (selectedPatches && selectedPatches.length > 0) {
      body.selected_patches = selectedPatches;
    }
    const res = await api.post(endpoints.lp.excelApply(planId), body);
    return res.data;
  },
  getExcelDiffPreview: async (planId, previewId) => {
    const res = await api.get(endpoints.lp.excelDiff(planId), { params: { preview_id: previewId } });
    return res.data;
  },
};

export default api; 